import './bootstrap';
import '../css/app.css';
import './global';

import { createApp, h } from 'vue';
import { createInertiaApp, router } from '@inertiajs/vue3';
import { applyColorSchemeIfNeeded } from './theme';
import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { ZiggyVue } from '../../vendor/tightenco/ziggy';
import { definePreset } from '@primeuix/themes'
import PrimeVue from 'primevue/config'
import ToastService from 'primevue/toastservice';
import ConfirmationService from 'primevue/confirmationservice';
import Aura from '@primeuix/themes/aura'

const appName = import.meta.env.VITE_APP_NAME || 'Laravel';

const AskBro = definePreset(Aura, {
    semantic: {
      colorScheme: {
        primary: {
          '50': '#eff2f2',
          '100': '#d8dbdc',
          '200': '#b3babe',
          '300': '#7e8a91',
          '400': '#4b5155',
          '500': '#313639',
          '600': '#2a2d30',
          '700': '#242629',
          '800': '#202123',
          '900': '#191a1b',
          '950': '#111214',
        },
        accent: {
          '50': '#fffdf8',
          '100': '#fef6e8',
          '200': '#fcecc4',
          '300': '#f8df9a',
          '400': '#f0d476',
          '500': '#f0cc68',
          '600': '#dcb84d',
          '700': '#b8973e',
          '800': '#967a33',
          '900': '#7a652c',
          '950': '#4a3d1a',
        }
      }
    },
    components: {
      divider: {
        border: {
          color: '{primary.800}'
        }
      },
      card: {
        background: '{primary.700}',
      },
      menu: {
        background: '{primary.900}',
        border: {
          color: '{black}'
        },
        separator: {
          border: {
            color: '{primary.700}'
          }
        },
        submenu: {
          label: {
            color: '{primary.100} !important'
          }
        },
        item: {
          color: '{primary.200}',
          focus: {
            background: '{primary.800}'
          }
        }
      },
      badge:{
        primary:{
          background: '{primary.300} !important',
          color: '{primary.950} !important',
        }
      },
      button: {
        danger: {
          background: '{red.950} !important',
          color: '{red.400} !important',
          borderColor: '{red.900} !important',
          hoverBackground: '{red.900} !important',
          hoverBorderColor: '{red.800} !important',
          hoverColor: '{red.300} !important',
          activeBackground: '{red.900} !important',
          activeColor: '{red.300} !important',
          activeBorderColor: '{red.800} !important'
        },
        primary: {
          background: '{primary.500} !important',
          color: '{primary.250} !important',
          hoverBackground: '{primary.400} !important',
          hoverColor: '{primary} !important',
          activeBackground: '{primary.400} !important',
          activeBorderColor: '{primary.500} !important'
        },
        outlined: {
          primary: {
            borderColor: '{primary.400} !important',
            color: '{primary.300} !important',
          }
        }
      },
      multiselect: {
        borderColor: '{primary.800} !important'
      },
      chip: {
        background: '{primary.900} !important',
        removeIconColor: '{red.600} !important'
      },
      toggleswitch: {
        background: '{primary.950} !important',
        borderColor: '{primary.800} !important',
        handleBackground: '{primary.600} !important',
        handleCheckedBackground: '{primary.200} !important',
        checked: {
          background: '{primary.600} !important',
          borderColor: '{primary.600} !important'
        }
      },
      inputtext: {

        background: '{primary.700}',
        border: {
          color: '{primary.800}'
        }
      },
      textarea: {
        borderColor: '{primary.900} !important',
      },
      inputnumber: {
        buttonBorderColor: '{transparent} !important',
        buttonHoverBackground: '{transparent} !important'
      },
      paginator: {
        background: '{primary.950}',
        borderRadius: '0px',
        padding: '4px 0px'
      },
      datepicker: {
        panel: {
          background: '{primary.950}',
          border: {
            color: '{primary.800}'
          }
        },
        date: {
          selected: {
            background: '{primary.500}'
          }
        },
        header: {
          background: '{primary.950}'
        }
      },
      breadcrumb: {
        background: '{transparent}',
        item: {
          color: '{primary.400}'
        },
        padding: '0px'
      },
      select: {
        padding: {
          x: '10px',
          y: '6px'
        },
        border: {
          color: '{primary.800}'
        },
        dropdown: {
          // width: 0,
          color: '{white}',
          hover: {
            border: '{primary.800}'
          }
        },
        overlay: {
          background: '{primary.950}'
        },
        option: {
          focus: {
            background: '{primary.900}'
          },
          selected: {
            background: '{primary.900}',
            focus: {
              background: '{primary.900}'
            }
          }
        }
      },
      tag: {
        success: {
          color: '{accent.400} !important',
          background: '{accent.950} !important'
        },
        font: {
          size: '12px'
        },
        primary: {
          color: '{primary.300} !important',
          background: '{primary.950} !important'
        },
        info: {
          color: '{blue.300} !important',
          background: '{blue.950} !important'
        },
        warn: {
          background: '{yellow.950} !important',
          color: '{yellow.300} !important'
        },
        danger: {
          background: '{red.950} !important',
          color: '{red.300} !important'
        }
      },
      tabs: {
        tablistBackground: '{transparent}',
        tabpanelBackground: '{transparent}',
        tabpanelPadding: '0px',
        tabBorderColor: '{primary.800}',
        tablistBorderColor: '{primary.800}',
        activeBarHeight: '2px'
      },
      datatable: {
        row: {
          background: '{black}',
          hoverBackground: '#170C04'
        }
      }
    }
  })


createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: (name) => resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue')),
    setup({ el, App, props, plugin }) {
        const pinia = createPinia()
        pinia.use(piniaPluginPersistedstate)

        const app = createApp({ render: () => h(App, props) })

        app
            .use(pinia)
            .use(plugin)
            .use(ToastService)
            .use(ConfirmationService)
            .use(ZiggyVue)
            .use(PrimeVue, {
                theme: {
                  preset: AskBro,
                  options: {
                    cssLayer: {
                      name: 'primevue',
                      order: 'tailwind-base, primevue, tailwind-utilities'
                    }
                  }
                },
                ripple: true
            });

        if (typeof localStorage !== 'undefined') {
            const storedTheme = localStorage.getItem('theme');
            if (storedTheme) {
                applyColorSchemeIfNeeded(storedTheme);
            }
        }

        app.mount(el);

        router.on('finish', () => {
            if (typeof localStorage === 'undefined') {
                return;
            }
            const storedTheme = localStorage.getItem('theme');
            if (storedTheme) {
                applyColorSchemeIfNeeded(storedTheme);
            }
        });

        return app;
    },
    progress: {
        color: '#4B5563',
    },
});
